

import { Component, Vue, Watch, Prop } from "vue-property-decorator"
import { namespace, Getter, Action } from "vuex-class"

// @ts-ignore
import VuePerfectScrollbar from "vue-perfect-scrollbar"
import vSelect from "vue-select"

import SkeletonLoader from "@/components/SkeletonLoader.vue"
import DataTable from "../DataTable.vue"

import FieldMapping from "./FieldMapping.vue"
import EventMapping from "./EventMapping.vue"
import IntegrationConfiguration from "./IntegrationConfiguration.vue"
import OutputConfiguration from "./OutputConfiguration.vue"
import NotifyOnFailure from "./NotifyOnFailure.vue"

import { IntegrationSettings, IntegrationWithSettings, IntegrationColumn, InFieldConfiguration, IntegrationEventMapping, SyncObjectType, FilePathMappingType } from "@/types/integration"

import api from "@/services/api"
import integrationApi from "@/services/api_Integrations"

import cloneDeep from "lodash/cloneDeep"
import { DataSource, Field, Team, Template } from "@/types"
import { CaseColumn, caseColumns } from "@/types/caseColumns"
import { TenantFeatures } from "@/types/tenantFeatures"
import FilePathMapping from "./FilePathMapping.vue"

const setup = namespace("setup")

@Component({
	components: {
		"v-select": vSelect,
		"scrollbar": VuePerfectScrollbar,
		"data-table": DataTable,
		"skeleton": SkeletonLoader,
		"configuration": IntegrationConfiguration,
		"event-mapping": EventMapping,
		"field-mapping": FieldMapping,
		"file-path-mapping": FilePathMapping,
		"output-configuration": OutputConfiguration,
		"notify-on-failure": NotifyOnFailure
	}
})
export default class IntegrationSetup extends Vue {
	$refs!: {
		dataTable: DataTable
	}

	@Getter getIsFeatureEnabled!: (feature: TenantFeatures) => boolean;
	TenantFeatures = TenantFeatures;
	SyncObjectType = SyncObjectType;

	@setup.State
	fields!: Field[];

	@setup.Getter("getTeams")
	teams!: Team[];

	@setup.Mutation
	addField!: (field: Field) => void;

	private loading: boolean = false;
	private integrations: IntegrationWithSettings[] = [];
	private selectedIntegration: IntegrationWithSettings | null = null;

	private templates: Template[] = []
	private dataSources: DataSource[] = []

	configureMappingsEvent: IntegrationEventMapping | null = null
	unsavedEventSpecificFieldMappingChanges: boolean = false

	get columns () {
		return [
			{ key: "settings.enabled", label: "Enabled", dataType: "checkbox", sortable: true },
			{ key: "integration.title", label: "Title", dataType: "text", sortable: true, readonly: true },
			{ key: "integration.description", label: "Description", dataType: "text-area", sortable: true, readonly: true, inModal: () => false }
		]
	}

	get caseColumns (): CaseColumn[] {
		return [
			...caseColumns,
			...this.fields.map(field => {
				return {
					key: "fields." + field.id,
					label: field.title,
					dataType: field.dataType!,
					dynamicField: true
				}
			})
		]
	}

	get assignableColumns (): CaseColumn[] {
		const columns = [
			...caseColumns.filter(c => c.key === "title" || c.key === "summary"),
			{
				key: "$atttachments",
				label: "Attachments",
				dataType: "file-only",
				dynamicField: false
			}
		]

		if (this.getIsFeatureEnabled(TenantFeatures.AllowDynamicTemplateSelectionForWebhook) && this.selectedIntegration?.integration.inbound?.supportDynamicFieldSelection) {
			columns.push(
				{
					key: "templateId",
					label: "Select Template",
					dataType: "text",
					dynamicField: false
				}
			)
		}

		return columns
	}

	private get showAllFields (): boolean {
		if (this.configureMappingsEvent && this.configureMappingsEvent.extensions) {
			return this.configureMappingsEvent.extensions.showAllFields || false
		}
		return false
	}

	private set showAllFields (value: boolean) {
		if (this.configureMappingsEvent && this.configureMappingsEvent.extensions) {
			this.$set(this.configureMappingsEvent.extensions, "showAllFields", value)
		}
	}

	private get cancelEditWarning (): string | null {
		if (!this.unsavedEventSpecificFieldMappingChanges) {
			return null
		}
		const isCsvSync = this.selectedIntegration?.integration.csvSyncOptions?.supportCsvSync

		return isCsvSync
			? "You appear to have been making changes to column-field mappings. Any such changes will be lost if not saved."
			: "You appear to have been making changes to event-specific field mappings. Any such changes will be lost if not saved."
	}

	public async mounted () {
		this.loading = true
		try {
			this.integrations = await integrationApi.integrationsGet()

			api.categories().then(templates => this.templates = templates)
			api.dataSources().then(dataSources => this.dataSources = dataSources)
		} finally {
			this.loading = false
		}
	}

	selectIntegration (integration: IntegrationWithSettings) {
		const integrationClone = cloneDeep(integration)
		if (!integrationClone.settings) {
			integrationClone.settings = {
				integrationId: integrationClone.integration.id,
				enabled: true,
				notifyOnFailure: [],
				notifyByEmail: false,
				configuration: {},
				inFieldMapping: {},
				eventMapping: {},
				filePathMappings: [],
				outConfiguration: {}
			}
		}

		return this.selectedIntegration = integrationClone
	}

	async createField (field: IntegrationColumn): Promise<string> {
		const newField = {
			title: field.title,
			description: `${field.title} from ${this.selectedIntegration!.integration.title} `,
			dataType: field.dataType === "any" ? "text" : field.dataType,
			dataSourceId: null,
			dataSourceTitle: null,
			definitionId: null,
			definitionTitle: null,
			allowFiles: false,
			sortByDescending: false,
			permissions: {
				record: { view: [], edit: [] },
				case: { view: [], edit: [] }
			},
			extensions: field.dataType === "user-role"
				? {
					makeAssignee: false,
					hasSubroles: false,
					isSingleUser: false
				}
				: null
		} as Field

		newField.id = await api.fieldSave(newField)

		this.addField(newField)
		return newField.id
	}

	private async saveIntegration (integration: IntegrationWithSettings) {
		await integrationApi.integrationsSave(integration.settings!)

		const integrationData = this.integrations.find(i => i.integration.id === integration.integration.id)
		if (integrationData) {
			integrationData.settings = integration.settings
		}

		this.unsavedEventSpecificFieldMappingChanges = false
	}

	private setFileUploadPermission (settings: IntegrationSettings, fileKey: string, value: string[] | null) {
		if (!settings.fileUploadSettings) {
			Vue.set(settings, "fileUploadSettings", {})
		}
		if (!settings.fileUploadSettings![fileKey]) {
			Vue.set(settings.fileUploadSettings!, fileKey, {
				permissions: null
			})
		}

		Vue.set(settings.fileUploadSettings![fileKey], "permissions", value)
	}

	/**
	 * @summary Sets the inFieldConfiguration at integration level or event level.
	 * @param keyAndConfig The field mapping key and configuration to set.
	 * @param event The event to set the inFieldConfiguration for. If null, the integration-level configuration is set.
	 */
	setInFieldConfigurationForField (keyAndConfig: { fieldKey: string, fieldConfig: InFieldConfiguration }, event: IntegrationEventMapping | null = null) {
		// Set inFieldConfiguration if undefined
		let inFieldConfiguration = event ? event.inFieldConfiguration : this.selectedIntegration!.settings!.inFieldConfiguration
		if (!inFieldConfiguration) {
			inFieldConfiguration = {}
			if (event) {
				event.inFieldConfiguration = inFieldConfiguration
			} else {
				this.selectedIntegration!.settings!.inFieldConfiguration = inFieldConfiguration
			}
		}

		if (event) {
			this.unsavedEventSpecificFieldMappingChanges = true
		}

		Vue.set(inFieldConfiguration as any, keyAndConfig.fieldKey, keyAndConfig.fieldConfig)
	}

	private configureEventSpecificFieldMappings (event: IntegrationEventMapping) {
		if (!event.inFieldConfiguration) {
			event.inFieldConfiguration = {}
		}

		if (!event.extensions) {
			event.extensions = {}
		}

		this.configureMappingsEvent = cloneDeep(event)
	}

	private updateEventSpecificFieldMapping (eventKey: string, fieldMapping: { [key: string]: string }) {
		const eventMapping = this.selectedIntegration!.settings!.eventMapping[eventKey]
		Vue.set(eventMapping, "inFieldMapping", fieldMapping)
		Vue.set(eventMapping, "inFieldConfiguration", this.configureMappingsEvent!.inFieldConfiguration)
		Vue.set(eventMapping, "extensions", this.configureMappingsEvent!.extensions)

		this.unsavedEventSpecificFieldMappingChanges = true
	}

	private get getCsvSyncMappingObjects () : unknown[] {
		if (this.selectedIntegration && this.selectedIntegration.integration.csvSyncOptions) {
			switch (this.selectedIntegration.integration.csvSyncOptions.syncObjectType) {
				case SyncObjectType.DataSource:
					return this.dataSources.filter(dataSource => dataSource.definitionId)
				default:
					return []
			}
		}
		return []
	}

	private get showCSVSyncTab () {
		return !!this.selectedIntegration && !!this.selectedIntegration.integration.csvSyncOptions // && this.selectedIntegration.integration.csvSyncOptions.supportCsvSync
	}

	private handleCsvSyncFilePathMappingChange (newFilePathMappings: FilePathMappingType[]) {
		if (this.selectedIntegration && this.selectedIntegration?.integration.csvSyncOptions.supportCsvSync && this.selectedIntegration.settings) {
			this.selectedIntegration.settings.filePathMappings = newFilePathMappings
		}
	}
}
